import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
// 👉 CORRECCIÓN: Se agregó 'Zap' a las importaciones de lucide-react
import { ArrowLeft, Check, Play, Zap } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';

const EquipoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [equipo, setEquipo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMedia, setActiveMedia] = useState(null);

  useEffect(() => {
    const fetchDetalle = async () => {
      try {
        const response = await apiService.getEquipoDetalle(id);
        if (response.estado === 1) {
          setEquipo(response.data);
          // Establecer el media principal como activo por defecto
          setActiveMedia({
            type: response.data.imagen_principal_es_video || /\.(mp4|webm|mov|avi)$/i.test(response.data.imagen_principal) ? 'video' : 'image',
            url: response.data.imagen_principal_url
          });
        }
      } catch (error) {
        console.error('Error cargando detalle:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetalle();
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div 
          className="w-12 h-12 border-4 border-yellow-600 border-t-yellow-500 rounded-full" 
          animate={{ rotate: 360 }} 
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }} 
        />
      </div>
    );
  }

  if (!equipo) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <h2 className="text-2xl font-bold mb-4">Equipo no encontrado</h2>
        <Link to="/" className="text-yellow-500 hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  // Parsear especificaciones si vienen como string
  let specs = {};
  try {
    specs = typeof equipo.especificaciones === 'string' ? JSON.parse(equipo.especificaciones) : (equipo.especificaciones || {});
  } catch (e) {
    specs = {};
  }

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Breadcrumb / Volver */}
        <motion.button 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-gray-400 hover:text-yellow-500 transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Volver</span>
        </motion.button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Columna Izquierda: Galería de Medios */}
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            {/* Media Principal */}
            <div className="relative aspect-video bg-neutral-900 rounded-2xl overflow-hidden border border-yellow-600/20 mb-4">
              {activeMedia?.type === 'video' ? (
                <video src={activeMedia.url} className="w-full h-full object-cover" autoPlay muted loop playsInline controls />
              ) : (
                <img src={activeMedia?.url} alt={equipo.nombre} className="w-full h-full object-cover" />
              )}
            </div>

            {/* Miniaturas (Galería) */}
            {equipo.galeria && equipo.galeria.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                {/* Miniatura del principal */}
                <button 
                  onClick={() => setActiveMedia({ type: equipo.imagen_principal_es_video ? 'video' : 'image', url: equipo.imagen_principal_url })}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${activeMedia?.url === equipo.imagen_principal_url ? 'border-yellow-500' : 'border-transparent hover:border-yellow-600/50'}`}
                >
                  {equipo.imagen_principal_es_video ? (
                    <div className="w-full h-full bg-black flex items-center justify-center relative">
                      <video src={equipo.imagen_principal_url} className="w-full h-full object-cover opacity-70" />
                      <Play className="w-6 h-6 text-white absolute" fill="white" />
                    </div>
                  ) : (
                    <img src={equipo.imagen_principal_url} alt="Principal" className="w-full h-full object-cover" />
                  )}
                </button>

                {/* Miniaturas de la galería */}
                {equipo.galeria.map((img, idx) => (
                  <button 
                    key={img.id}
                    onClick={() => setActiveMedia({ type: img.es_video ? 'video' : 'image', url: img.url_completa })}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${activeMedia?.url === img.url_completa ? 'border-yellow-500' : 'border-transparent hover:border-yellow-600/50'}`}
                  >
                    {img.es_video ? (
                      <div className="w-full h-full bg-black flex items-center justify-center relative">
                        <video src={img.url_completa} className="w-full h-full object-cover opacity-70" />
                        <Play className="w-5 h-5 text-white absolute" fill="white" />
                      </div>
                    ) : (
                      <img src={img.url_completa} alt={img.alt_texto || equipo.nombre} className="w-full h-full object-cover" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Columna Derecha: Información */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="inline-block bg-yellow-600/20 text-yellow-500 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              {equipo.categoria_nombre}
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {equipo.nombre}
            </h1>

            <div className="prose prose-invert max-w-none mb-8">
              <div dangerouslySetInnerHTML={{ __html: equipo.descripcion }} className="text-gray-300 leading-relaxed text-lg" />
            </div>

            {/* Especificaciones Técnicas */}
            {Object.keys(specs).length > 0 && (
              <div className="bg-neutral-900/50 border border-yellow-600/20 rounded-2xl p-6 mb-8">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  {/* Ahora Zap está correctamente importado y funcionará */}
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Especificaciones Técnicas
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(specs).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-gray-400 text-sm capitalize block">{key.replace(/_/g, ' ')}</span>
                        <span className="text-white font-medium">
                          {Array.isArray(value) ? value.join(', ') : value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Call to Action */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/contacto" 
                state={{ equipoInteres: equipo.nombre }}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-bold text-center px-8 py-4 rounded-full hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-yellow-500/25"
              >
                Cotizar este equipo
              </Link>
              <Link 
                to="/equipos" 
                className="flex-1 border border-yellow-600/50 text-yellow-500 font-semibold text-center px-8 py-4 rounded-full hover:bg-yellow-600/10 transition-all duration-300"
              >
                Ver más equipos
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EquipoDetalle;